/**
 * A package, and what putting one on a quote produces.
 *
 * `docs/catalog-and-packages.md` §3.1. A service is the container a quote line
 * lives in; a **package** is what the company actually sells, and that is very
 * often several trips rather than one — drop the dumpster off, swap it every
 * week while it is on site, pick it up at the end. A recipe that can only make
 * lines inside a single container cannot say that, which is why the rental
 * line used to have to invent its own visits.
 *
 * Everything here is pure. `expandPackage()` is given the package and the two
 * numbers the quoter typed, and returns the visits, services and lines to
 * create; the server writes them and prices them, and `recalcQuote()` is still
 * the only thing that produces money. The arithmetic inside a line has not
 * changed and is not re-implemented here: `quantityFor()` in
 * `service-quantity.ts` is the only way a quantity is worked out (AGENTS.md).
 *
 * **Why timing is a rule and not a date.** A package is written once and used
 * on quotes that start in April and quotes that start in October. "Seven days
 * after the first visit" survives that; "April 13" does not. `resolveTiming()`
 * is where a rule meets a particular quote's start date and becomes the
 * columns `quote_visits` actually holds.
 */
import { z } from 'zod';
import { quantityFor } from './service-quantity.js';
// ---------------------------------------------------------------------------
// When a visit happens
// ---------------------------------------------------------------------------
export const VISIT_TIMING_KINDS = [
    'on_date', 'offset', 'package_start', 'recurring', 'rental_end',
];
/**
 * The five ways a package visit can say when it happens.
 *
 * `on_date` is the visit whose date nobody knows yet — the office picks it on
 * the quote. `package_start` is the day the quoter chose when adding the
 * package. `offset` counts days from the first visit or from the one before
 * it. `recurring` repeats, either a fixed number of times or for as long as
 * the units are on site. `rental_end` is the trip that closes it out, which
 * has a date only if the rental has an end date.
 *
 * A discriminated union rather than a table of nullable columns: the five
 * kinds carry different fields, and "every field nullable" says nothing about
 * which combinations are real.
 */
export const visitTimingSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('on_date') }),
    z.object({
        kind: z.literal('offset'),
        days: z.number().int().min(0).max(3650),
        from: z.enum(['first_visit', 'previous']),
    }),
    z.object({ kind: z.literal('package_start') }),
    z.object({
        kind: z.literal('recurring'),
        every: z.number().int().min(1).max(52),
        unit: z.enum(['day', 'week', 'month']),
        /** ISO weekday, Monday 1 – Sunday 7. Weekly rules only. */
        onDow: z.number().int().min(1).max(7).optional(),
        /** Until the units come back, or a fixed number of times. */
        while: z.enum(['rental', 'count']),
        count: z.number().int().min(1).max(999).optional(),
    }),
    z.object({ kind: z.literal('rental_end') }),
    /*
     * **The When, on a package** (visits contract §10.1). The quote's
     * Starts · Repeats · Ends with no dates in it: a package is dated when it
     * goes on a quote. `ref` is this visit's stable name inside the package —
     * the builder re-inserts a package's visits on every save, so an anchor
     * cannot point at an id — and `anchorRef` names another visit's `ref`.
     * The five kinds above stay valid and expand as they always did; a visit is
     * written as `when` only when somebody edits it (no backfill).
     */
    z.object({
        kind: z.literal('when'),
        ref: z.string().uuid(),
        starts: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('start') }),
            z.object({
                kind: z.literal('after'),
                anchorRef: z.string().uuid(),
                event: z.enum(['done', 'starts']),
                lag: z.number().int().min(0).max(3650),
                unit: z.enum(['days', 'business_days', 'weeks']),
            }),
            z.object({ kind: z.literal('term_end') }),
            z.object({ kind: z.literal('on_call') }),
        ]),
        repeats: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('none') }),
            z.object({
                kind: z.literal('every'),
                every: z.number().int().min(1).max(366),
                unit: z.enum(['day', 'week', 'month', 'year']),
                weekdays: z.array(z.number().int().min(1).max(7)).max(7),
            }),
        ]),
        ends: z.discriminatedUnion('kind', [
            z.object({ kind: z.literal('count'), count: z.number().int().min(1).max(999) }),
            z.object({ kind: z.literal('term_end') }),
            z.object({ kind: z.literal('never') }),
        ]),
    }),
]);
/** How long a visit takes: a fixed number of days, or so many per unit. */
export const daysRuleSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('fixed'), days: z.number().int().min(1).max(365) }),
    z.object({ kind: z.literal('per_measure'), per: z.number().finite().positive() }),
]);
/**
 * How long the units stay.
 *
 * Defined here rather than waiting for the rentals feature because
 * `resolveTiming` cannot answer "when does the pickup happen" without it, and
 * a rule that reads the term through `any` is a rule that compiles when the
 * term's shape changes underneath it.
 */
export const rentalTermSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('end_date'), endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }),
    z.object({ kind: z.literal('days'), days: z.number().int().min(1).max(3650) }),
    z.object({ kind: z.literal('open') }),
]);
// ---------------------------------------------------------------------------
// What a line scales by
// ---------------------------------------------------------------------------
/**
 * Which of the quoter's two numbers a line multiplies.
 *
 * `quantity` is "how many" — four dumpsters, three trees. `measurement` is
 * "how much" — 640 square feet of patio. `once` is the line that happens
 * regardless: one mobilisation, one permit.
 */
/**
 * What a package line may name.
 *
 * The five a quote line has, plus `rental` — which a *quote* line does not
 * have, because a rental on a quote is an equipment line carrying a
 * `rental_equipment_id`. The package has to keep them apart: the two live in
 * different catalog tables, and a recipe that said "equipment" could not say
 * which table to look in.
 */
export const PACKAGE_LINE_KINDS = [
    'item', 'labor', 'deployment', 'equipment', 'sub', 'rental',
];
export const SCALES_BY = ['quantity', 'measurement', 'once'];
export const SCALES_BY_LABELS = {
    quantity: 'Per unit',
    measurement: 'Per measure',
    once: 'Once',
};
export const NO_SERVICE_NOTES = { customer: '', internal: '', crew: '' };
/**
 * What a package's service says, note by note (0127): the package's own
 * wording where it has one, the catalog service's where it has none.
 *
 * The one reading of the override, so the builder's popup and the expansion
 * onto a quote cannot disagree about which words a customer gets.
 */
export function packageServiceNotes(service, catalog = service.catalogNotes ?? NO_SERVICE_NOTES) {
    return {
        customer: service.noteCustomer ?? catalog.customer,
        internal: service.noteInternal ?? catalog.internal,
        crew: service.noteCrew ?? catalog.crew,
    };
}
/**
 * A note as a package stores it (0127): null where the words are the
 * catalog's, so the package goes on following the catalog rather than keeping
 * a frozen copy of what it said the day somebody pressed Done; anything else,
 * '' included, is the package's own. Compared trimmed, because the write
 * schema trims what it stores.
 */
export function noteOverride(text, catalog) {
    if (text === null || text === undefined)
        return null;
    return text.trim() === catalog.trim() ? null : text;
}
export const NO_PROPOSAL_OVERRIDES = {
    timing: null, priceView: null, detail: null, phraseId: null, phraseText: null,
};
/** A new plan of a kind, every setting at its default (the backfill's and `+ Add a billing plan`'s). */
export function blankPackageBillingPlan(name, kind = 'completion') {
    return {
        name, kind, depositPct: null, depositFlat: null, restMode: 'last', completionMode: 'each',
        partPeriod: 'full', periodUnit: 'month', periodEvery: 1, periodCount: null, periodBasis: 'even',
        dayOfMonth: null, dayOfWeek: null, tmTrigger: 'visit_completion', capAmount: null,
        termsMode: 'customer', termsDays: 30, milestones: [],
    };
}
/** A new plan's name, by what it does (§6): `When done` · `Monthly` · `Stages` · `Deposit` · … */
export function defaultPlanName(kind, periodUnit = 'month') {
    switch (kind) {
        case 'completion': return 'When done';
        case 'period': return periodUnit === 'week' ? 'Weekly' : periodUnit === 'day' ? 'Daily' : 'Monthly';
        case 'milestones': return 'Stages';
        case 'deposit_balance': return 'Deposit';
        default: return 'Time & materials';
    }
}
// ---------------------------------------------------------------------------
// Dates, in UTC and without a library
// ---------------------------------------------------------------------------
const DAY = 86_400_000;
function parseDay(iso) {
    return Date.parse(`${iso}T00:00:00Z`);
}
function toDay(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function addDays(iso, days) {
    return toDay(parseDay(iso) + days * DAY);
}
function addMonths(iso, months) {
    const date = new Date(parseDay(iso));
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + months);
    // The 31st of a 30-day month is the 30th, not the 1st of the month after.
    const lastOfMonth = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
    date.setUTCDate(Math.min(day, lastOfMonth));
    return toDay(date.getTime());
}
/** ISO weekday of a date: Monday 1 … Sunday 7. */
function isoDow(iso) {
    return new Date(parseDay(iso)).getUTCDay() || 7;
}
/** The first date on or after `iso` that falls on `dow`. */
function onOrAfterDow(iso, dow) {
    return addDays(iso, (dow - isoDow(iso) + 7) % 7);
}
// ---------------------------------------------------------------------------
// resolveTiming
// ---------------------------------------------------------------------------
/**
 * The `quote_visits` columns a timing rule produces for one particular quote.
 *
 * `trigger` is the part that is easy to miss: a visit whose end or date is
 * decided by something other than the calendar says so, rather than carrying a
 * date somebody invented. A service visit that runs `while: 'rental'` on an
 * open term has no end date and `trigger = 'term_end'`; the pickup on that
 * same term has no date at all and `trigger = 'on_call'`. Both are honest, and
 * both are what makes `openEnded` true on the visit so the builder prints "∞"
 * instead of a count nobody can justify.
 */
export function resolveTiming(timing, ctx) {
    const once = (on, trigger = 'date') => ({
        schedule: 'one_time', startsOn: on, endsOn: on, targetOn: on, qty: 1, trigger,
    });
    switch (timing.kind) {
        case 'on_date':
            // Nobody has said when yet; the office picks it on the quote.
            return once(null);
        case 'package_start':
            return once(ctx.startsOn);
        case 'offset': {
            const from = timing.from === 'previous' ? (ctx.previousOn ?? ctx.startsOn) : ctx.startsOn;
            return once(addDays(from, timing.days));
        }
        case 'when':
            return whenSchedule(timing, ctx);
        case 'rental_end': {
            const term = ctx.rental?.term;
            if (term?.kind === 'end_date')
                return once(term.endsOn);
            if (term?.kind === 'days')
                return once(addDays(ctx.startsOn, term.days));
            // An open term ends when the customer calls. That is not a date.
            return once(null, 'on_call');
        }
        case 'recurring': {
            const schedule = recurringSchedule(timing);
            const startsOn = timing.unit === 'week' && timing.onDow
                ? onOrAfterDow(ctx.startsOn, timing.onDow)
                : ctx.startsOn;
            if (timing.while === 'count') {
                const count = Math.max(1, timing.count ?? 1);
                return {
                    schedule,
                    startsOn,
                    endsOn: lastOccurrence(startsOn, timing, count),
                    targetOn: null,
                    qty: count,
                    trigger: 'date',
                };
            }
            // While the units are on site: the term decides, if it knows.
            const term = ctx.rental?.term;
            const endsOn = term?.kind === 'end_date' ? term.endsOn
                : term?.kind === 'days' ? addDays(ctx.startsOn, term.days)
                    : null;
            return {
                schedule,
                startsOn,
                endsOn,
                targetOn: null,
                qty: 1,
                trigger: endsOn ? 'date' : 'term_end',
            };
        }
    }
}
/**
 * The legacy columns a package `when` comes to before the quote dates it — a
 * first answer for the insert. `writeVisitWhen` then writes the real When and
 * re-derives them, once the anchor it follows has an id (§10.1).
 */
function whenSchedule(timing, ctx) {
    const on = timing.starts.kind === 'start' ? ctx.startsOn : null;
    const trigger = timing.starts.kind === 'term_end' ? 'term_end'
        : timing.starts.kind === 'on_call' ? 'on_call' : 'date';
    if (timing.repeats.kind === 'none' || timing.starts.kind === 'term_end') {
        return { schedule: 'one_time', startsOn: on, endsOn: on, targetOn: on, qty: 1, trigger };
    }
    const repeats = timing.repeats;
    const schedule = repeats.unit === 'week' && repeats.every === 1 ? 'weekly'
        : repeats.unit === 'week' && repeats.every === 2 ? 'biweekly'
            : repeats.unit === 'month' && repeats.every === 1 ? 'monthly' : 'custom';
    const qty = timing.ends.kind === 'count' ? timing.ends.count : 1;
    return {
        schedule, startsOn: on, endsOn: null, targetOn: null, qty,
        trigger: timing.ends.kind === 'count' ? trigger : 'term_end',
    };
}
/**
 * A package `when` as the quote's When, with each anchor's `ref` turned into
 * whatever the caller names visits by — `v2` on the builder, a quote visit's
 * id on a quote. `At the start` is the quote's undated *On a date*, which is
 * what makes `visitWhenPhrase` read it `Every 6 weeks · 5 visits` (§4.4's "no
 * date yet (package)" row) with no second phrase function.
 */
export function visitWhenOfPackage(timing, idOfRef, lengthDays = 1, startsOn = null) {
    const { starts, repeats, ends } = timing;
    return {
        starts: starts.kind === 'start' ? { kind: 'date', on: startsOn }
            : starts.kind === 'after'
                ? { kind: 'after', anchorId: idOfRef(starts.anchorRef) ?? starts.anchorRef, event: starts.event,
                    lag: starts.lag, unit: starts.unit }
                : { kind: starts.kind },
        repeats: repeats.kind === 'none' ? { kind: 'none' } : { ...repeats },
        ends: repeats.kind === 'none' ? { kind: 'count', count: 1 } : { ...ends },
        lengthDays: Math.max(1, lengthDays),
    };
}
/**
 * The When block's draft back as a package `when`: a date start is `start`
 * (a package has no dates), `On specific dates` and an end on a date cannot be
 * said and are read as their nearest — the block never offers them on a
 * package, so this only meets them if a caller passes one.
 */
export function packageTimingOfWhen(when, ref, refOfId) {
    const { starts, repeats, ends } = when;
    return {
        kind: 'when',
        ref,
        starts: starts.kind === 'date' ? { kind: 'start' }
            : starts.kind === 'after'
                ? { kind: 'after', anchorRef: refOfId(starts.anchorId) ?? starts.anchorId, event: starts.event,
                    lag: starts.lag, unit: starts.unit }
                : { kind: starts.kind },
        repeats: repeats.kind === 'every'
            ? { kind: 'every', every: repeats.every, unit: repeats.unit, weekdays: repeats.weekdays }
            : { kind: 'none' },
        ends: ends.kind === 'term_end' || ends.kind === 'never' ? { kind: ends.kind }
            : { kind: 'count', count: ends.kind === 'count' ? ends.count : 1 },
    };
}
/**
 * How the When block shows one of the five older kinds (§10.1's table).
 * `anchorOf` names the visit an offset counts from — the package's first
 * visit, or the one above — by whatever the caller names visits by.
 */
export function packageWhenOfOldTiming(timing, anchorOf) {
    switch (timing.kind) {
        case 'offset': {
            const anchor = anchorOf(timing.from);
            return anchor
                ? { kind: 'when', anchorKey: anchor,
                    starts: { kind: 'after', anchorRef: anchor, event: 'starts', lag: timing.days, unit: 'days' },
                    repeats: { kind: 'none' }, ends: { kind: 'count', count: 1 } }
                : { kind: 'when', anchorKey: null, starts: { kind: 'start' }, repeats: { kind: 'none' },
                    ends: { kind: 'count', count: 1 } };
        }
        case 'recurring':
            return {
                kind: 'when', anchorKey: null, starts: { kind: 'start' },
                repeats: { kind: 'every', every: timing.every, unit: timing.unit,
                    weekdays: timing.unit === 'week' && timing.onDow ? [timing.onDow] : [] },
                ends: timing.while === 'rental' ? { kind: 'term_end' } : { kind: 'count', count: Math.max(1, timing.count ?? 1) },
            };
        case 'rental_end':
            return { kind: 'when', anchorKey: null, starts: { kind: 'term_end' }, repeats: { kind: 'none' },
                ends: { kind: 'count', count: 1 } };
        default:
            return { kind: 'when', anchorKey: null, starts: { kind: 'start' }, repeats: { kind: 'none' },
                ends: { kind: 'count', count: 1 } };
    }
}
/** A repeat rule as one of the schedules `quote_visits` can hold. */
function recurringSchedule(timing) {
    if (timing.unit === 'week' && timing.every === 1)
        return 'weekly';
    if (timing.unit === 'week' && timing.every === 2)
        return 'biweekly';
    if (timing.unit === 'month' && timing.every === 1)
        return 'monthly';
    // Every third week, every ten days: real, and not one of the four names.
    return 'custom';
}
function lastOccurrence(startsOn, timing, count) {
    const steps = count - 1;
    if (timing.unit === 'month')
        return addMonths(startsOn, timing.every * steps);
    const days = timing.unit === 'week' ? timing.every * 7 : timing.every;
    return addDays(startsOn, days * steps);
}
/**
 * The visits, services and lines a package produces on one quote.
 *
 * Pure, and it produces no money: the server prices each line through the same
 * catalog path an ordinary line takes, so a package line and a hand-added line
 * of the same thing cost the same. What this decides is *how many* — and it
 * decides it with `quantityFor()`, the one formula, so a package line and a
 * service recipe line scale identically.
 *
 * Visits come out in the package's own order, and `offset … from: 'previous'`
 * reads the one before it in that order — which is why this is a loop and not
 * a `map`.
 */
export function expandPackage(pkg, input) {
    const qty = pkg.asks === 'quantity' || pkg.asks === 'both' ? (input.qty ?? 1) : 1;
    const measure = pkg.asks === 'measurement' || pkg.asks === 'both' ? (input.measure ?? 0) : 0;
    const visits = [];
    let previousOn = null;
    for (const spec of pkg.visits) {
        const schedule = resolveTiming(spec.timing, {
            startsOn: input.startsOn, previousOn, rental: input.rental,
        });
        previousOn = schedule.startsOn ?? previousOn;
        visits.push({
            packageVisitId: spec.id ?? null,
            name: spec.name,
            role: spec.role,
            schedule,
            durationDays: resolveDays(spec.daysRule ?? null, measure),
            billingPlanKind: spec.billingPlanKind ?? null,
            billingPlanId: spec.billingPlanId ?? null,
            when: spec.timing.kind === 'when'
                ? {
                    ref: spec.timing.ref,
                    anchorRef: spec.timing.starts.kind === 'after' ? spec.timing.starts.anchorRef : null,
                    when: visitWhenOfPackage(spec.timing, (ref) => ref, resolveDays(spec.daysRule ?? null, measure) ?? 1, input.startsOn),
                }
                : null,
            notes: spec.notes,
            optional: spec.optional ?? false,
            proposal: spec.proposal ?? NO_PROPOSAL_OVERRIDES,
            services: spec.services.map((service) => ({
                serviceId: service.serviceId,
                divisionId: service.divisionId,
                optional: service.optional ?? false,
                noteCustomer: service.noteCustomer ?? null,
                noteInternal: service.noteInternal ?? null,
                noteCrew: service.noteCrew ?? null,
                lines: service.lines.map((line) => expandLine(line, qty, measure)),
            })),
        });
    }
    return visits;
}
/** What one recipe line comes to at the quoter's numbers. */
export function expandLine(line, qty, measure) {
    const factor = basisFor(line.scalesBy, qty, measure);
    return {
        kind: line.kind,
        refId: line.refId,
        packageLineSpec: line,
        scalesBy: line.scalesBy,
        qty: quantityFor({ baseQty: line.baseQty, qtyPerUnit: line.qtyPerUnit, bands: line.bands }, factor),
        factor,
        optional: line.optional,
        showOnProposal: line.showOnProposal,
        priceOverride: line.priceOverride ?? null,
        handling: handlingOf(line),
    };
}
/** Consumes stock, resolved: the line's own answer, else a material is consumed. */
export function handlingOf(line) {
    if (line.handling !== undefined && line.handling !== null)
        return line.handling;
    return line.kind === 'item' ? 'consume' : null;
}
/** Which number a line multiplies. `once` multiplies one, which is the point. */
export function basisFor(scalesBy, qty, measure) {
    if (scalesBy === 'quantity')
        return qty;
    if (scalesBy === 'measurement')
        return measure;
    return 1;
}
function resolveDays(rule, measure) {
    if (!rule)
        return null;
    if (rule.kind === 'fixed')
        return rule.days;
    return Math.max(1, Math.round(rule.per * measure));
}
// ---------------------------------------------------------------------------
// What the client may send
// ---------------------------------------------------------------------------
export const packageAddSchema = z.object({
    packageId: z.string().uuid(),
    qty: z.number().finite().positive().max(99_999).optional(),
    measure: z.number().finite().min(0).max(99_999_999).optional(),
    /** Which of the property's measurements the measure came from, if any. */
    measurementId: z.string().uuid().nullish(),
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const packageInstancePatchSchema = packageAddSchema.partial().omit({ packageId: true })
    .extend({
    /**
     * Null is **Clear**: the package keeps every visit and line, and a line
     * that grows with the size goes back to what it needs at any size.
     */
    measure: z.number().finite().min(0).max(99_999_999).nullish(),
});
//# sourceMappingURL=packages.js.map